import { useCallback, useEffect } from 'react'
import useSWR, { preload } from 'swr'

import type { IntentDictionary, OptionalFilters } from '@Globals/SearchIntent'

// Client loader for the intent boosts. The matcher is behind a dynamic import so
// it stays out of the shared bundle; the dictionary and chunk are warmed at idle
// from the header so the first keystroke already has boosts. Callers get [] until
// both are ready, which just means an unboosted search.

const dictionaryKey = (locale: string) => `/static/search-intent/${locale}.json`

// Mirrors the matcher's unsupported set so these locales skip the dictionary
// fetch and the matcher chunk entirely — the whitespace tokenizer cannot
// segment CJK queries.
const UNSUPPORTED_LOCALES = new Set(['ja-JP', 'ko-KR'])

const fetchDictionary = async (url: string): Promise<IntentDictionary | null> => {
  const response = await fetch(url)
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    throw new Error(`search-intent dictionary request failed: ${response.status}`)
  }
  return response.json()
}

const swrOptions = {
  revalidateOnFocus: false,
  errorRetryCount: 2,
}

type IntentModule = typeof import('@Globals/SearchIntent')

let intentModule: Promise<IntentModule | null> | undefined
let loaded: IntentModule | null = null

const loadIntentModule = () => {
  if (!intentModule) {
    intentModule = import('@Globals/SearchIntent')
      .then((module) => {
        loaded = module
        return module
      })
      .catch(() => {
        intentModule = undefined
        return null
      })
  }

  return intentModule
}

const onIdle = (callback: () => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 3000 })
    return
  }

  window.setTimeout(callback, 2000)
}

export const preloadSearchIntent = (locale: string) => {
  if (UNSUPPORTED_LOCALES.has(locale)) {
    return
  }

  onIdle(() => {
    preload(dictionaryKey(locale), fetchDictionary).catch(() => {})
    loadIntentModule()
  })
}

export const useSearchIntent = (locale: string) => {
  const supported = !UNSUPPORTED_LOCALES.has(locale)
  const { data: dictionary } = useSWR(supported ? dictionaryKey(locale) : null, fetchDictionary, swrOptions)

  useEffect(() => {
    if (supported) {
      loadIntentModule()
    }
  }, [supported])

  return useCallback((query: string): OptionalFilters => {
    if (!loaded || !dictionary) {
      return []
    }

    return loaded.intentOptionalFilters(query, dictionary, locale)
  }, [dictionary, locale])
}
